import type { PerspectiveCamera } from 'three'
import { EventDispatcher, Vector3 } from 'three'
import * as THREE from 'three'
import type { CameraViewData, Vec3Data } from '@/entities/model3d'
import type { Analytics } from '@/plugins/analytics'
import { tweenCamera } from '@/lib/three-d-editor/tween-camera'
import { tweenCameraLinear } from '@/lib/three-d-editor/tween-camera-linear'
import type { ControlEventMap, SetCameraOptions, ViewerControls } from '@/lib/three-d-resources/controls/viewerControls'
import { ControlEvent, DEFAULT_MODEL_TWEEN_DURATION } from '@/lib/three-d-resources/controls/viewerControls'
import type { CameraUpdateMessage } from '@/composables/useConnectSession/setup-channels'
import { compareVec3, roundAbsolute } from '@/lib/compare-camera-value'
import CameraControls from 'camera-controls'
import type { MouseButtons } from 'camera-controls/dist/types'
import PatchedCameraControls from '@/lib/three-d-editor/patched-camera-controls'

CameraControls.install({ THREE })

type MouseButtonAction = MouseButtons[keyof MouseButtons]

class ReadonlyControls extends EventDispatcher<ControlEventMap> implements ViewerControls {
  cameraControls: CameraControls
  getCenterHitPoint: () => Vector3 | null = () => null
  private _hasBeenControlledAtSomePoint = false
  private _isTweening = false
  private _currentTweenDuration: number | null = null
  private _cancelTween: () => void = () => {}
  private _lastInteraction: MouseButtonAction | null = null

  constructor(private camera: PerspectiveCamera, private _analytics?: typeof Analytics) {
    super()
    this.cameraControls = new PatchedCameraControls(this.camera)
  }

  get isTweening() {
    return this._isTweening
  }

  set isTweening(newVal: boolean) {
    if (newVal !== this._isTweening) {
      this._isTweening = newVal
      this.dispatchEvent({ type: newVal ? ControlEvent.StartTween : ControlEvent.StopTween })
    }
  }

  get target(): [number, number, number] {
    const target = new Vector3()
    this.cameraControls?.getTarget(target)
    return this.cameraControls ? target.toArray() : [0, 0, 0]
  }

  set target([x, y, z]: [number, number, number]) {
    if (!this.cameraControls) return
    this.cameraControls.setTarget(x, y, z)
  }

  onMouseDown = (e: MouseEvent): void => {
    if (e.buttons & 1) {
      this._lastInteraction = this.cameraControls.mouseButtons.left
    } else if (e.buttons & 2) {
      this._lastInteraction = this.cameraControls.mouseButtons.right
    } else if (e.buttons & 4) {
      this._lastInteraction = this.cameraControls.mouseButtons.middle
    }
    if (this.cameraControls?.enabled && this.isTweening) {
      this._cancelTween()
      this.isTweening = false
    }
  }

  //remove resourceManager
  attach(element: HTMLElement) {
    this.cameraControls.disconnect()
    this.cameraControls.connect(element)
    this.cameraControls.smoothTime = 0.1
    this.cameraControls.maxDistance = 20_000
    this.cameraControls.minDistance = 20
    this.cameraControls.dollyToCursor = true
    this.cameraControls.dollySpeed = 0.25
    this.cameraControls.minPolarAngle = 0.1 // 6 degrees from vertical to prevent awkward transitions
    this.cameraControls.maxPolarAngle = Math.PI - 0.1 // 174 degrees
    this.cameraControls.mouseButtons.middle = CameraControls.ACTION.TRUCK

    document.addEventListener('keydown', this._keydownHandler)
    document.addEventListener('keyup', this._keyupHandler)
    window.addEventListener('blur', this._documentFocusHandler)

    this.cameraControls.addEventListener('update', async () => {
      this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })

      // Don't broadcast while tweening. This messes with transitions for participants
      if (this.isTweening) {
        return
      }

      this.cameraControls.getTarget(this._target1, false)
      this.dispatchEvent({
        type: ControlEvent.CameraControlEvent,
        payload: { camera: this.camera, target: this._target1, duration: this._currentTweenDuration ?? 0 },
      })

      this._currentTweenDuration = null
    })

    this.cameraControls.addEventListener('control', e => {
      this._hasBeenControlledAtSomePoint = true
      this._isTweening = false
    })

    this.cameraControls.addEventListener('rest', () => {
      if (!this.cameraControls.enabled) return

      // check if the control was not a rotation
      if (this._lastInteraction !== CameraControls.ACTION.ROTATE) {
        const point = this.getCenterHitPoint()
        if (!point) return
        this.cameraControls.setTarget(point.x, point.y, point.z)
      }

      this.dispatchEvent({ type: ControlEvent.StopTween })
    })

    this.cameraControls.addEventListener('sleep', () => {
      // Needed to fire one last CameraControlEvent rotation after being controlled
      // 'sleep' gets fired on initial setup. We only want to fire this after the user has controlled
      if (this._hasBeenControlledAtSomePoint && !this._isTweening) {
        this.cameraControls.getTarget(this._target1, false)
        this.dispatchEvent({
          type: ControlEvent.CameraControlEvent,
          payload: { camera: this.camera, target: this._target1, duration: this._currentTweenDuration ?? 0 },
        })
      }
      this._isTweening = false
      this.dispatchEvent({ type: ControlEvent.StopTween })
    })

    // resourceManager.addEventListener('complete', this.onModelLoaded)
    element.addEventListener('mousedown', this.onMouseDown)
  }

  setCameraData(
    cameraData: CameraViewData,
    { currentState = {}, tweenDuration = DEFAULT_MODEL_TWEEN_DURATION, disableControls = true }: SetCameraOptions = {}
  ): void {
    if (!this.cameraControls) {
      return
    }
    const { cameraControls, camera } = this

    const usingReconnect = !!(currentState.position || currentState.target || currentState.fov)
    const position: Vec3Data = currentState.position ?? cameraData.position
    const target: Vec3Data = currentState.target ?? cameraData.target
    const fov: number = currentState.fov ?? cameraData.fov.x

    const cameraTarget = cameraControls.getTarget(undefined as any)
    const isEquivalentPosition =
      compareVec3(camera.position, position) && compareVec3(cameraTarget, target) && roundAbsolute(camera.fov) === roundAbsolute(fov)

    this._currentTweenDuration = isEquivalentPosition ? 0.1 : tweenDuration

    this._cancelTween()
    if (tweenDuration && !usingReconnect) {
      this.isTweening = true
      this.dispatchEvent({ type: ControlEvent.SetCameraTweenStart })
      if (disableControls) {
        cameraControls.enabled = false
      }
      this._cancelTween = tweenCamera(
        camera,
        cameraControls,
        {
          position,
          fov: { x: fov, y: fov },
          target,
        },
        this._currentTweenDuration,
        () => {
          this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
        },
        () => {
          if (disableControls) {
            cameraControls.enabled = true
            this.dispatchEvent({ type: ControlEvent.SetCameraTweenComplete })
          }
        }
      )
      return
    }

    // synchronous operation when 0 duration
    const { x: x0, y: y0, z: z0 } = position
    const { x: x1, y: y1, z: z1 } = target
    cameraControls.setLookAt(x0, y0, z0, x1, y1, z1)
    camera.fov = fov
    camera.updateProjectionMatrix()
  }

  followControl({ fov, target, position, duration }: CameraUpdateMessage): void {
    const { cameraControls, camera } = this
    if (!cameraControls || this._isTweening) {
      return
    }

    this._cancelTween()
    this._cancelTween = tweenCameraLinear(camera, cameraControls, { fov: { x: fov, y: fov }, position, target }, duration)
  }

  update(delta: number): boolean {
    return this.cameraControls?.update(delta) ?? false
  }

  detach() {
    this._cancelTween()

    // if (this.viewer) {
    //   this.viewer.renderer.domElement.removeEventListener('mousedown', this.onMouseDown)
    //   this.viewer.removeEventListener('model-loaded', this.onModelLoaded)
    //   this.viewer = null
    // }

    this.cameraControls.dispose()

    document.removeEventListener('keydown', this._keydownHandler)
    document.removeEventListener('keyup', this._keyupHandler)
    window.removeEventListener('blur', this._documentFocusHandler)
  }

  setMaxDistance(sceneSize: Vector3) {
    const isZeroPoint = sceneSize.x === 0 && sceneSize.y === 0 && sceneSize.z === 0
    if (!this.cameraControls || isZeroPoint) return

    const largestCoordinate = Math.max(sceneSize.x, sceneSize.y, sceneSize.z)
    this.cameraControls.maxDistance = largestCoordinate * 8
  }

  private _buttonsDown = 0
  private _keydownHandler = (event: KeyboardEvent) => {
    if (!this.cameraControls) return
    if (TruckModifierKeys.hasOwnProperty(event.key)) {
      this._buttonsDown |= TruckModifierKeys[event.key as keyof typeof TruckModifierKeys]
    }
    if (this._buttonsDown > 0) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK
    }
  }
  private _keyupHandler = (event: KeyboardEvent) => {
    if (!this.cameraControls) return
    if (TruckModifierKeys.hasOwnProperty(event.key)) {
      this._buttonsDown &= ~TruckModifierKeys[event.key as keyof typeof TruckModifierKeys]
    }
    if (this._buttonsDown <= 0) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE
    }
  }
  private _documentFocusHandler = () => {
    if (this.cameraControls) {
      this._buttonsDown = 0
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE
    }
  }

  // working objects
  private _target1 = new Vector3()
}

enum TruckModifierKeys {
  Shift = 1 << 0,
  Meta = 1 << 1,
  Control = 1 << 2,
}

let instance: ReadonlyControls

export function getReadOnlyControls() {
  if (!instance) throw new Error('Init ReadonlyControls before getting instance')

  return instance
}

export function initReadOnlyControls(camera: PerspectiveCamera, analytics?: typeof Analytics) {
  // if (instance) throw new Error('ReadonlyControls already initialized')

  instance = new ReadonlyControls(camera, analytics)
}

export type { ReadonlyControls }
